import { useState, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { selectCachedProduct, selectSelectedVariantId } from '../../store/selectors';
import { setCachedProduct } from '../../store/slices/productSlice';
import { setSelectedProductVariants, setSelectedVariantId, setSelectedProduct } from '../../store/slices/gameSlice';
import { ApiManager } from '../../services/ApiManager';
import { useSession } from '../useSession';
import { getCurrentLanguage, getCurrentCountry } from '../../constants/localization';
import { GameProduct, ProductData, VariantImage } from '../../components/product/types';
import { normalizeProductId } from '../../components/product/utils';

export const useProductData = (productId: string) => {
  const dispatch = useAppDispatch();
  const { sessionId, getFormattedExperiments } = useSession();

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [currentProduct, setCurrentProduct] = useState<GameProduct | undefined>(undefined);
  const [productData, setProductData] = useState<ProductData | null>(null);
  const [gameProducts, setGameProducts] = useState<Record<string, GameProduct>>({});
  const [variantImages, setVariantImages] = useState<VariantImage[]>([]);
  const [selectedVariations, setSelectedVariations] = useState<Record<string, string>>({});

  const normalizedProductId = normalizeProductId(productId);

  // Redux selectors for caching
  const cachedAllProducts = useAppSelector(selectCachedProduct('allProducts'));
  const cachedProductData = useAppSelector(selectCachedProduct(`product_${normalizedProductId}`));
  const selectedVariantId = useAppSelector(selectSelectedVariantId);

  // Helper function to get metafield value by key and namespace
  const getMetafieldValue = (key: string, namespace?: string): string | null => {
    if (!currentProduct?.metafields?.edges) {
      return null;
    }

    const metafield = currentProduct.metafields.edges.find((edge) => {
      if (namespace) {
        return edge.node.key === key && edge.node.namespace === namespace;
      }
      return edge.node.key === key;
    });

    return metafield?.node.value || null;
  };

  // Helper functions to get review data from metafields
  const getReviewsCount = (): number => {
    const count = getMetafieldValue('rating_count', 'reviews');
    return count ? parseInt(count, 10) : 0;
  };

  const getReviewsAverage = (): number => {
    // Handle the complex rating structure from the API
    const ratingMetafield = currentProduct?.metafields?.edges.find(
      (edge) => edge.node.key === 'rating' && edge.node.namespace === 'reviews'
    );

    if (!ratingMetafield) {
      return 0;
    }

    try {
      // Try to parse if it's stored as JSON string
      const ratingData = JSON.parse(ratingMetafield.node.value);
      if (ratingData && typeof ratingData === 'object' && ratingData.value) {
        return parseFloat(ratingData.value);
      }
    } catch (e) {
      // If parsing fails, try to use it as a direct value
      const rating = parseFloat(ratingMetafield.node.value);
      if (!isNaN(rating)) {
        return rating;
      }
    }

    return 0;
  };

  // Helper function to get pre-order date from current variant
  const getPreOrderDate = (): string | null => {
    if (!currentProduct?.variants?.edges) {
      return null;
    }

    let targetVariant;

    // First priority: if a specific variant is selected (via selectedVariantId)
    if (selectedVariantId) {
      targetVariant = currentProduct.variants.edges.find((edge) => {
        return edge.node.id === selectedVariantId;
      })?.node;
    }
    // Second priority: if a size variation is selected
    else if (selectedVariations.Size) {
      targetVariant = currentProduct.variants.edges.find((edge) => {
        return edge.node.title === selectedVariations.Size;
      })?.node;
    }
    // Third priority: check other variations that might be selected
    else if (Object.keys(selectedVariations).length > 0) {
      // Find a variant that matches any selected variations
      for (const [variationName, variationValue] of Object.entries(selectedVariations)) {
        const matchingVariant = currentProduct.variants.edges.find((edge) => {
          return edge.node.title === variationValue;
        });
        if (matchingVariant) {
          targetVariant = matchingVariant.node;
          break;
        }
      }
    }
    // No fallback: if no variant is specifically selected, return null (no pre-order info)
    else {
      return null;
    }

    if (!targetVariant?.metafields?.edges) {
      return null;
    }

    // Look for next_available_date in fabrikator namespace
    const preOrderMetafield = targetVariant.metafields.edges.find((edge) => {
      return edge.node.key === 'next_available_date' && edge.node.namespace === 'fabrikator';
    });

    return preOrderMetafield?.node.value || null;
  };

  // Fetch product data
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch single product from API with required parameters
        const productData = await ApiManager.products.getProduct(productId, {
          country: getCurrentCountry(),
          language: getCurrentLanguage(),
          experiments: getFormattedExperiments(),
          sessionId: sessionId || undefined,
        });

        // Convert the API format to match the current component structure
        const transformedProduct: GameProduct = {
          id: productData.id.toString(),
          title: productData.title,
          description: productData.descriptionHtml?.replace(/<[^>]*>/g, '') || '',
          descriptionHtml: productData.descriptionHtml || '',
          handle: productData.handle || productData.title.toLowerCase().replace(/\s+/g, '-'),
          media: {
            edges:
              productData.images?.map((image: any, index: number) => ({
                node: {
                  id: `image-${index}`,
                  image: {
                    url: image.url,
                    altText: image.altText || productData.title,
                  },
                },
              })) || [],
          },
          variants: {
            edges:
              productData.variants?.map((variant: any, index: number) => ({
                node: {
                  id: variant.id.toString(),
                  title: variant.title,
                  price: (variant.price.amountInCents / 100).toString(),
                  sku: '',
                  position: index + 1,
                  availableForSale: variant.availableForSale,
                  inventoryQuantity: variant.availableForSale ? 10 : 0,
                  selectedOptions: variant.selectedOptions || [],
                  metafields: {
                    edges: variant.metafields
                      ? Object.entries(variant.metafields).flatMap(([namespace, fields]: [string, any]) =>
                          Object.entries(fields).map(([key, field]: [string, any], fieldIndex: number) => ({
                            node: {
                              key,
                              value: field.value?.toString() || '',
                              type: field.type || 'string',
                              id: `variant-${variant.id}-${namespace}-${key}-${fieldIndex}`,
                              namespace,
                            },
                          }))
                        )
                      : [],
                  },
                },
              })) || [],
          },
          options:
            productData.options?.map((option: any) => ({
              name: option.name,
              values: option.values.map((value: any) => value.name),
            })) || [],
          metafields: {
            edges: productData.metafields
              ? Object.entries(productData.metafields).flatMap(([namespace, fields]: [string, any]) =>
                  Object.entries(fields).map(([key, field]: [string, any], index: number) => ({
                    node: {
                      key,
                      value:
                        field.type === 'rating' && field.value
                          ? JSON.stringify(field.value)
                          : field.type === 'rich_text_field' && field.value && typeof field.value === 'object'
                          ? JSON.stringify(field.value)
                          : field.value?.toString() || '',
                      type: field.type || 'string',
                      id: `${namespace}-${key}-${index}`,
                      namespace,
                    },
                  }))
                )
              : [],
          },
          other_colors: productData.metafields?.custom?.other_colors
            ? {
                references: {
                  edges: productData.metafields.custom.other_colors.value.map((productId: string) => ({
                    node: {
                      id: productId,
                      handle: '',
                    },
                  })),
                },
              }
            : null,
          priceRange: {
            minVariantPrice: {
              amount: productData.variants?.[0] ? (productData.variants[0].price.amountInCents / 100).toString() : '0',
              currencyCode: productData.variants?.[0]?.price.currencyCode || 'EUR',
            },
          },
          featuredImage: productData.images?.[0]
            ? {
                url: productData.images[0].url,
                altText: productData.images[0].altText || productData.title,
              }
            : null,
        };

        const productsObject: Record<string, GameProduct> = {
          [transformedProduct.id]: transformedProduct,
          [normalizedProductId]: transformedProduct,
        };

        setGameProducts(productsObject);

        // Load variant images data for other colors functionality
        try {
          const variantImagesResponse = await fetch('/data/variants-lilja.json');
          const variantImagesData = await variantImagesResponse.json();
          setVariantImages(variantImagesData.variantImages || []);
        } catch (error) {
          console.error('Error loading variant images:', error);
          setVariantImages([]);
        }
      } catch (error) {
        console.error('Error fetching product data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [dispatch, productId, normalizedProductId, sessionId]);

  // Set current product when gameProducts changes
  useEffect(() => {
    if (isLoading) return;

    let currentProduct: GameProduct | undefined;

    if (gameProducts[normalizedProductId]) {
      currentProduct = gameProducts[normalizedProductId];
    } else {
      currentProduct = Object.values(gameProducts).find((product) => product.id.toString().includes(`${productId}`));
    }

    if (currentProduct) {
      setCurrentProduct(currentProduct);

      // Set this product as the selected product in Redux so StatusBar can access it
      dispatch(setSelectedProduct(currentProduct));

      // Extract variations from the options array
      const variations = currentProduct.options.map((option) => ({
        name: option.name,
        values: option.values,
      }));

      // Set initial selected variations
      const initialVariations: Record<string, string> = {};
      setSelectedVariations(initialVariations);

      // Clear selected variations in Redux when loading a product
      dispatch(setSelectedProductVariants(initialVariations));
      dispatch(setSelectedVariantId(null));

      const newProductData = {
        id: currentProduct.id,
        title: currentProduct.title,
        price: parseFloat(currentProduct.variants.edges[0]?.node?.price || '0'),
        image: currentProduct.media.edges[0]?.node?.image?.url || 'https://placehold.co/400x400?text=No+Image',
        description: currentProduct.description,
        descriptionHtml: currentProduct.descriptionHtml,
        variations: variations,
        currencyCode: 'EUR',
      };

      setProductData(newProductData);
    } else {
      setProductData(null);
    }
  }, [productId, normalizedProductId, gameProducts, isLoading, dispatch]);

  // Send analytics when product is loaded (only on initial load, not on variant changes)
  useEffect(() => {
    if (productData && currentProduct) {
      const sendAnalytics = async () => {
        try {
          // Use the first variant for initial product view
          const firstVariant = currentProduct.variants.edges[0]?.node;

          if (firstVariant) {
            await ApiManager.analytics.playmerce_product_view({
              product_id: parseInt(currentProduct.id),
              product_title: currentProduct.title,
              variant_id: parseInt(firstVariant.id),
              variant_title: firstVariant.title,
              price: {
                amount: parseFloat(firstVariant.price),
                currency: 'EUR',
              },
            });
          }
        } catch (error) {
          console.error('Failed to send product view analytics:', error);
        }
      };

      sendAnalytics();
    }
  }, [productData, currentProduct]); // Removed selectedVariations from dependencies

  // Handle variation changes
  const handleVariationChange = async (variationName: string, value: string) => {
    const newVariations = {
      ...selectedVariations,
      [variationName]: value,
    };
    setSelectedVariations(newVariations);

    // Store selected variations in Redux
    dispatch(setSelectedProductVariants(newVariations));

    // Find the corresponding variant ID from the API data
    if (currentProduct) {
      // For products with multiple options, we need to find a variant that matches ALL selected options
      const matchingVariant = currentProduct.variants.edges.find((edge) => {
        const variant = edge.node;

        // Check if we have all required options selected
        const allOptionsSelected = currentProduct.options.every((option) => {
          return newVariations[option.name] !== undefined;
        });

        if (!allOptionsSelected) {
          // If not all options are selected yet, don't select a variant
          return false;
        }

        // First try to match using selectedOptions (most accurate)
        if (variant.selectedOptions && variant.selectedOptions.length > 0) {
          const optionsMatch = variant.selectedOptions.every((selectedOption: any) => {
            return newVariations[selectedOption.name] === selectedOption.value;
          });

          if (optionsMatch && variant.selectedOptions.length === Object.keys(newVariations).length) {
            return true;
          }
        }

        // Fallback: Try to match based on the combined title format (e.g., "XS / Tie back - adjustable")
        const expectedTitle = currentProduct.options.map((option) => newVariations[option.name]).join(' / ');

        if (variant.title === expectedTitle) {
          return true;
        }

        // Second fallback: if the above doesn't work, try matching against individual values
        // This handles cases where the title format might be different
        if (currentProduct.options.length === 1) {
          return variant.title === value;
        }

        return false;
      });

      if (matchingVariant) {
        dispatch(setSelectedVariantId(matchingVariant.node.id));

        // Send variant product view analytics
        try {
          await ApiManager.analytics.playmerce_variant_product_view({
            product_id: parseInt(currentProduct.id),
            product_title: currentProduct.title,
            variant_id: parseInt(matchingVariant.node.id),
            variant_title: matchingVariant.node.title,
            price: {
              amount: parseFloat(matchingVariant.node.price),
              currency: 'EUR',
            },
          });
        } catch (error) {
          console.error('Failed to send variant product view analytics:', error);
        }
      } else {
        // Clear selected variant if no match found (partial selection state)
        dispatch(setSelectedVariantId(null));
      }
    }
  };

  // Handle color variant changes
  const handleColorVariantChange = async (variantProductId: string) => {
    if (!variantProductId || variantProductId === currentProduct?.id) {
      return;
    }

    try {
      setIsLoading(true);

      // Extract product ID from Shopify GID format
      const newProductId = variantProductId.split('/').pop() || variantProductId;

      // Fetch new product data
      const productData = await ApiManager.products.getProduct(newProductId, {
        country: getCurrentCountry(),
        language: getCurrentLanguage(),
        experiments: getFormattedExperiments(),
        sessionId: sessionId || undefined,
      });

      // Transform the new product data (reusing the same transformation logic)
      const transformedProduct: GameProduct = {
        id: productData.id.toString(),
        title: productData.title,
        description: productData.descriptionHtml?.replace(/<[^>]*>/g, '') || '',
        descriptionHtml: productData.descriptionHtml || '',
        handle: productData.handle || productData.title.toLowerCase().replace(/\s+/g, '-'),
        media: {
          edges:
            productData.images?.map((image: any, index: number) => ({
              node: {
                id: `image-${index}`,
                image: {
                  url: image.url,
                  altText: image.altText || productData.title,
                },
              },
            })) || [],
        },
        variants: {
          edges:
            productData.variants?.map((variant: any, index: number) => ({
              node: {
                id: variant.id.toString(),
                title: variant.title,
                price: (variant.price.amountInCents / 100).toString(),
                sku: '',
                position: index + 1,
                availableForSale: variant.availableForSale,
                inventoryQuantity: variant.availableForSale ? 10 : 0,
                selectedOptions: variant.selectedOptions || [],
                metafields: {
                  edges: variant.metafields
                    ? Object.entries(variant.metafields).flatMap(([namespace, fields]: [string, any]) =>
                        Object.entries(fields).map(([key, field]: [string, any], fieldIndex: number) => ({
                          node: {
                            key,
                            value: field.value?.toString() || '',
                            type: field.type || 'string',
                            id: `variant-${variant.id}-${namespace}-${key}-${fieldIndex}`,
                            namespace,
                          },
                        }))
                      )
                    : [],
                },
              },
            })) || [],
        },
        options:
          productData.options?.map((option: any) => ({
            name: option.name,
            values: option.values.map((value: any) => value.name),
          })) || [],
        metafields: {
          edges: productData.metafields
            ? Object.entries(productData.metafields).flatMap(([namespace, fields]: [string, any]) =>
                Object.entries(fields).map(([key, field]: [string, any], index: number) => ({
                  node: {
                    key,
                    value:
                      field.type === 'rating' && field.value
                        ? JSON.stringify(field.value)
                        : field.type === 'rich_text_field' && field.value && typeof field.value === 'object'
                        ? JSON.stringify(field.value)
                        : field.value?.toString() || '',
                    type: field.type || 'string',
                    id: `${namespace}-${key}-${index}`,
                    namespace,
                  },
                }))
              )
            : [],
        },
        other_colors: productData.metafields?.custom?.other_colors
          ? {
              references: {
                edges: productData.metafields.custom.other_colors.value.map((productId: string) => ({
                  node: {
                    id: productId,
                    handle: '',
                  },
                })),
              },
            }
          : null,
        priceRange: {
          minVariantPrice: {
            amount: productData.variants?.[0] ? (productData.variants[0].price.amountInCents / 100).toString() : '0',
            currencyCode: productData.variants?.[0]?.price.currencyCode || 'EUR',
          },
        },
        featuredImage: productData.images?.[0]
          ? {
              url: productData.images[0].url,
              altText: productData.images[0].altText || productData.title,
            }
          : null,
      };

      // Update the game products state with the new product
      const newProductsObject = {
        ...gameProducts,
        [normalizedProductId]: transformedProduct,
        [productId]: transformedProduct,
        [transformedProduct.id]: transformedProduct,
      };
      setGameProducts(newProductsObject);
    } catch (error) {
      console.error('Error loading color variant:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return {
    isLoading,
    currentProduct,
    productData,
    variantImages,
    selectedVariations,
    getMetafieldValue,
    getReviewsCount,
    getReviewsAverage,
    getPreOrderDate,
    handleVariationChange,
    handleColorVariantChange,
  };
};
